import { forwardRef, useContext, useEffect, useState } from 'react';
import { Field, type FieldProps } from 'formik';
import isEmpty from 'lodash/isEmpty';

import { FormContext, isRequiredField } from './utils';
import SimpleMultiSelectInput from './SimpleMultiSelectInput';
import type { SimpleMultiSelectInputProps } from './SimpleMultiSelectInput';

/**
 * MultiSelectInput — обёртка над SimpleMultiSelectInput для работы с Formik.
 *
 * 🔧 НОВЫЕ СВОЙСТВА (с версии 2.1):
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ convertPrimitives: boolean (по умолчанию: false)            │
 * ├─────────────────────────────────────────────────────────────┤
 * │ Что делает:                                                 │
 * │   Автоматически конвертирует примитивы ↔ объекты для        │
 * │   react-select внутри компонента.                           │
 * │                                                             │
 * │ Когда использовать:                                         │
 * │   • true  — если передаёте/получаете массив строк/чисел    │
 * │             (типично для Formik: ["id1", "id2"])           │
 * │   • false — если передаёте/получаете массив объектов       │
 * │             [{value: "id1", label: "Название"}]            │
 * │                                                             │
 * │ Пример (режим Formik):                                      │
 * │   <MultiSelectInput                                         │
 * │     name="vendorCodes"                                      │
 * │     value={["runFlat", "tireMounting"]}                     │
 * │     convertPrimitives={true}  // ✅ включаем конвертацию   │
 * │     data={positions}                                        │
 * │     dataValue="vendorCode"                                  │
 * │     dataTitle="positionName"                                │
 * │   />                                                        │
 * └─────────────────────────────────────────────────────────────┘
 *
 * ┌─────────────────────────────────────────────────────────────┐
 * │ mapValuesToField: function(selectedOptions) → any          │
 * ├─────────────────────────────────────────────────────────────┤
 * │ Что делает:                                                 │
 * │   Кастомная трансформация значений ПЕРЕД записью в Formik.  │
 * │   Применяется ПОСЛЕ технической конвертации.                │
 * │                                                             │
 * │ Когда использовать:                                         │
 * │   • Нужна бизнес-логика: фильтрация, форматирование,       │
 * │     агрегация данных перед сохранением.                    │
 * │   • НЕ нужен, если достаточно просто массива ID/строк.     │
 * │                                                             │
 * │ Пример (сложная трансформация):                             │
 * │   <MultiSelectInput                                         │
 * │     convertPrimitives={true}                                │
 * │     mapValuesToField={(ids) =>                              │
 * │       ids.filter(id => id !== 'excluded')                  │
 * │          .map(id => `prefix_${id}`)                        │
 * │     }                                                       │
 * │   />                                                        │
 * └─────────────────────────────────────────────────────────────┘
 *
 * ⚠️  ВАЖНО:
 * • Не используйте mapValuesToField для простой конвертации
 *   объектов → примитивы, если уже включён convertPrimitives.
 * • Порядок выполнения: convertPrimitives → onChange →
 *   mapValuesToField → setFieldValue (Formik).
 * • По умолчанию (без convertPrimitives) компонент работает
 *   в старом режиме: ожидает и возвращает объекты {value, label}.
 *
 * 🧭 БЫСТРЫЙ ВЫБОР:
 *
 * [Formik, массив строк]
 *   → convertPrimitives={true}, mapValuesToField — опционально
 *
 * [Ручное управление, массив объектов]
 *   → convertPrimitives не указывать (false), mapValuesToField — по необходимости
 *
 * [Кастомная логика поверх примитивов]
 *   → convertPrimitives={true} + mapValuesToField={ваша функция}
 */

/**
 * Собственные пропсы Formik-обёртки мультиселекта.
 *
 * То, что MultiSelectInput достаивает из `{...props}` и обрабатывает сам, а НЕ пробрасывает
 * в SimpleMultiSelectInput как есть. Остальные пропсы (`value`, `onChange`, `error`, `label`,
 * `data`, `convertPrimitives`, ...) наследуются из {@link SimpleMultiSelectInputProps} —
 * ниже в {@link MultiSelectInputProps}.
 *
 * `value`/`onChange`/`error` здесь НЕ повторяются — они уже объявлены в SimpleMultiSelectInputProps
 * и концептуально «перехватываются» MultiSelectInput'ом (в деструктуризации вытаскиваются отдельно
 * от `...rest`), но типизируются тем же типом, что и у Simple — конфликта пересечения нет.
 *
 * `convertPrimitives`/`mapFieldToSelectedOptions` НЕ повторяются — они уже в SimpleMultiSelectInputProps
 * и пробрасываются в Simple как есть (обёртка их не перехватывает).
 */
export interface MultiSelectInputOwnProps {
  /** Ручной признак обязательности; иначе автодетект по validationSchema (isRequiredField). */
  required?: boolean;
  /** Yup-схема формы; берётся из FormContext, если не передана явно. */
  validationSchema?: any;
  /**
   * Колбэк вызывается, когда поле переходит в состояние ошибки валидации.
   * Получает текст ошибки (`meta.error`).
   */
  onValidateFail?: (error: unknown) => void;
  /** Начальное значение-массив; при передаче/изменении заполняет внутренний valueArray. */
  defaultValue?: unknown[];
  /**
   * Кастомная трансформация выбранного массива ПЕРЕД записью в Formik-поле.
   * Подробное описание — в JSDoc компонента выше.
   */
  mapValuesToField?: (selectedOptions: unknown) => unknown;
}

/**
 * Пропсы Formik-обёртки мультиселекта (MultiSelectInput).
 *
 * Расширяет {@link SimpleMultiSelectInputProps} своими Formik-специфичными полями.
 * `field`/`meta`/`form` инжектируются render-prop'ом `<Field>` во внутренний компонент
 * и НЕ входят в этот тип — внешний потребитель их не передаёт.
 *
 * Селекторы получают к этому типу доступ через `ComponentProps<typeof MultiSelectInput>`.
 */
export type MultiSelectInputProps = SimpleMultiSelectInputProps & MultiSelectInputOwnProps;

/** Внутренние пропсы: {@link MultiSelectInputProps} + `field`/`meta`/`form` из formik `<Field>`. */
type MultiSelectInputInnerProps = MultiSelectInputProps & FieldProps;

const MultiSelectInput = forwardRef<HTMLElement, MultiSelectInputInnerProps>((props, ref) => {
  const [valueArray, setValueArray] = useState<unknown[] | null>(null);
  const {
    field,
    form,
    meta,
    required,
    validationSchema,
    onValidateFail,
    defaultValue,
    mapValuesToField,
    ...rest
  } = props;

  const {
    submitCount,
    setFieldValue,
    setFieldTouched,
  } = form;

  const hasError = !isEmpty(meta.error)
    && ((meta.touched && (field.value !== undefined)) || (submitCount > 0));

  useEffect(() => {
    if (Array.isArray(valueArray)) {
      setFieldValue(field.name, typeof mapValuesToField === 'function' ? mapValuesToField(valueArray) : valueArray);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valueArray]);

  useEffect(() => {
    if (!meta.touched) {
      setFieldTouched(field.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [field.value]);

  useEffect(() => {
    if (hasError && (typeof onValidateFail === 'function')) {
      onValidateFail(meta.error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasError]);

  useEffect(() => {
    if (defaultValue) {
      setValueArray(defaultValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultValue]);

  return (
    <SimpleMultiSelectInput
      { ...field }
      { ...rest }
      ref={ ref }
      error={ hasError ? meta.error : null }
      required={ required || isRequiredField(validationSchema, field.name) }
      onChange={ e => setValueArray(e.target.value) }
    />
  );
});

const MultiSelectInputWrapper = forwardRef<HTMLElement, MultiSelectInputProps>((props, ref) => {
  // FormContext создаётся в utils.js (createContext() без дженерика); при allowJs:false
  // useContext возвращает unknown. Приводим к ожидаемой форме контекста формы
  // (Provider всегда подаёт значение; default — undefined, деструктуризация безопасна).
  const { validationSchema } = useContext(FormContext) as { validationSchema?: any };

  return (
    <Field { ...props }>
      { fieldProps => (
        <MultiSelectInput
          ref={ ref }
          validationSchema={ validationSchema }
          { ...fieldProps }
          { ...props }
        />
      ) }
    </Field>
  );
});

MultiSelectInputWrapper.displayName = 'MultiSelectInput';

export default MultiSelectInputWrapper;
